import { Accordion } from 'react-bootstrap-accordion';
import { Link } from 'react-router-dom';
import { ProgressBar } from 'react-bootstrap';
import { handleInputNumberOnly } from '@util/helper';
import { toast } from 'react-toastify';
import { useAPI, useCurrency, useHelper } from 'src/hooks';
import { useBalance } from 'src/hooks/useBalance';
import { useDebouncedCallback } from 'use-debounce';
import { useForm } from 'react-hook-form';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import SkeletonImage from '@com/utils/SkeletonImage';
import loadable from '@loadable/component';
import styled from '@emotion/styled';

const Footer = loadable(() => import('@com/footer/Footer'));
const FullSectionText = loadable(() => import('@com/FullSectionText'));
const Header = loadable(() => import('@com/header/Header'));
const LoadMoreBtn = loadable(() => import('@com/buttons/LoadMoreBtn'));
const LoadingContainer = loadable(() => import('@com/utils/LoadingContainer'));
const TopSection = loadable(() => import('@com/TopSection'));

const AccountContainer = styled.div`
  min-height: 250px;
  margin: 1rem;
  border-radius: 10px;
`;

const Title = styled.div`
  width: 95%;
  display: flex;
  overflow: hidden;
  flex-wrap: wrap;

  .total {
    background-color: var(--bg-section2);
    border-radius: 0.4em;
    padding: 0.3em 1em;
  }

  @media (max-width: 475px) {
    .currency {
      margin: auto;
    }
    .total {
      width: 100%;
      margin-top: 1rem;
    }
  }
`;

const DisplayCardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-gap: 0.5em;
  margin: 3rem 1rem 3rem 0;
`;

const DisplayCard = styled.div`
  background-color: var(--bg-section2);
  border-radius: 0.4em;

  @media (max-width: 557px) {
    font-size: 0.6em;
  }
`;

const WithdrawContainer = styled.div`
  @media (max-width: 925px) {
    flex-direction: column;

    button {
      width: 50%;
      margin-top: 3rem;
      margin-bottom: 3rem;
    }
  }
`;

function BalancePage(): JSX.Element {
  /* Define state / hook */
  const { t } = useTranslation();
  const { getCurrencyList } = useCurrency();
  const { getAmount, getBadge, getInvoiceDate } = useBalance();
  const { getBalance, getBalanceList, withdrawDeposit } = useAPI();
  const { displayCurrency } = useHelper();
  const { data: balanceData } = useQuery([getBalance.name], () => getBalance());
  const { data, isSuccess, isError, hasNextPage, fetchNextPage } =
    useInfiniteQuery(getBalanceList.name, getBalanceList, {
      getNextPageParam: (lastPage) => {
        return lastPage?.next_cursor;
      },
    });

  const { register, handleSubmit } = useForm();
  /* Custom Data Object for render or other usages */

  /* Render control function / logic */
  const onSubmit = useDebouncedCallback(
    async (dataList, currency) => {
      const amount: string = dataList[`${currency}_amount`];
      // FIXME: react-hook-hook will transform to '' if invalid number format?
      if (!amount) {
        toast.error(t('errorMsg.general.numberValidRequired'));
        return;
      }
      const result = await withdrawDeposit(Number(amount), currency);
      if (result) {
        toast.info(t('alertMessage.balance.withdrawSucceed'));
      }
    },
    500,
    { leading: true, trailing: false }
  );

  const getIconURL = (currency: string) => {
    return getCurrencyList()?.find((i) => i?.name === currency)?.icon;
  };

  /* Styling */

  /* Render */
  return (
    <>
      <Header />
      <TopSection sectionName={t('bankReceipt.amountStatement')} />
      <FullSectionText>
        <LoadingContainer isError={isError} isSuccess={isSuccess}>
          <>
            {balanceData && (
              <div className="col-md-12 flat-accordion2 mb-5 pb-5">
                <h2 className="bold fs-32 mb-5">{t('bankReceipt.balance')}</h2>
                <hr />
                {balanceData?.map((item) => {
                  const total =
                    Number(item?.balance) +
                    Number(item?.pending_balance) +
                    Number(item?.claimed_balance);
                  return (
                    <Accordion
                      key={item?.currency}
                      title={
                        (
                          <Title>
                            <div className="d-flex align-items-center currency">
                              <SkeletonImage
                                className="mr-3"
                                height={32}
                                size={[64, 128]}
                                src={getIconURL(item?.currency)}
                                width={32}
                              />
                              <b>{item?.currency}</b>
                            </div>
                            <div className="total ml-auto">
                              {displayCurrency(total, item?.currency)}
                            </div>
                          </Title>
                        ) as any
                      }
                    >
                      <AccountContainer className="author-profile w-100">
                        <ProgressBar animated className="w-100 mt-5 mb-4">
                          <ProgressBar
                            key={1}
                            animated
                            now={(Number(item?.claimed_balance) / total) * 100}
                            variant="success"
                          />
                          <ProgressBar
                            key={2}
                            animated
                            striped
                            now={(Number(item?.pending_balance) / total) * 100}
                            variant="warning"
                          />
                          <ProgressBar
                            key={3}
                            animated
                            now={(Number(item?.balance) / total) * 100}
                            variant="info"
                          />
                        </ProgressBar>
                        <DisplayCardContainer>
                          <DisplayCard>
                            <h2>{t('bankReceipt.claimed')}</h2>
                            <div className="text-success">
                              {displayCurrency(
                                item?.claimed_balance,
                                item?.currency
                              )}
                            </div>
                          </DisplayCard>
                          <DisplayCard>
                            <h2>{t('bankReceipt.pending')}</h2>
                            <div className="text-warning">
                              {displayCurrency(
                                item?.pending_balance,
                                item?.currency
                              )}
                            </div>
                          </DisplayCard>
                          <DisplayCard>
                            <h2>{t('bankReceipt.remainings')}</h2>
                            <div className="text-info">
                              {displayCurrency(item?.balance, item?.currency)}
                            </div>
                          </DisplayCard>
                        </DisplayCardContainer>
                        <WithdrawContainer className="d-flex align-items-center">
                          <h4 className="mr-4">
                            {t('bankReceipt.cashOutAmount')}
                          </h4>
                          <div className="mr-4">
                            <input
                              aria-required
                              id={`${item?.currency}_amount`}
                              max={Math.ceil(Number(item?.balance))}
                              step={1}
                              type="number"
                              onKeyDown={(e) => handleInputNumberOnly(e)}
                              {...register(`${item?.currency}_amount`)}
                            />
                          </div>
                          <button
                            className="sc-button loadmore fl-button pri-3"
                            type="submit"
                            onClick={() =>
                              handleSubmit(onSubmit)(item?.currency as any)
                            }
                          >
                            {t('bankReceipt.confirm')}
                          </button>
                        </WithdrawContainer>
                      </AccountContainer>
                    </Accordion>
                  );
                })}
              </div>
            )}

            <h2 className="bold fs-32 mb-5">
              {t('bankReceipt.historyRecord')}
            </h2>
            <hr />
            <div className="table-ranking">
              <div className="item flex my-3 h-auto" data-test="table-header">
                <h3 className="col-2">{t('bankReceipt.type')}</h3>
                <h3 className="col-3">{t('bankReceipt.category')}</h3>
                <h3 className="col-3">{t('bankReceipt.amount')}</h3>
                <h3 className="col-4">{t('bankReceipt.date')}</h3>
              </div>
              {data?.pages?.map((page) =>
                page?.data?.map((item, index: number) => (
                  <div key={index} className="fs-21">
                    <Link
                      className="item flex"
                      to={`/balance/${item?.record_number}`}
                    >
                      <div className="col-2">{getBadge(item?.type, 'md')}</div>
                      <div className="col-3">
                        <span>{item?.event}</span>
                      </div>
                      <div className="col-3">
                        {getAmount(item?.amount, item?.currency)}
                      </div>
                      <div className="col-4">
                        {getInvoiceDate(item?.created_at_timestamp)}
                      </div>
                    </Link>
                  </div>
                ))
              )}
            </div>
            <LoadMoreBtn
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
            />
          </>
        </LoadingContainer>
      </FullSectionText>
      <Footer />
    </>
  );
}

export default BalancePage;
